import * as z from 'zod';

import { AuthorizedContactId, BorrowerId, CompanyId, EntityId, LoanOwnerId, LosId, PoolId } from '../../BrandedIds';
import { type ProductType, SpecialLoanStatus } from '../../loan/Loan';
import { NonPerformanceStatus } from '../../loan/NonPerformance';
import { zodTemporalInstant, zodTemporalPlainDate } from '../../Temporal';
import { ExportableAddress } from '../util/ExportableAddress';
import { lakeColumn } from '../util/lakeColumn';
import { literalsCovering } from '../util/literalsCovering';
import { ExportableDeferredAmounts } from './ExportableDeferredAmounts';

const PRODUCT_TYPES = literalsCovering<NonNullable<ProductType>>()({
  current: ['fha', 'va', 'usda', 'pih', 'conv', 'heloc', 'bridge', 'businessPurpose', 'jumbo'],
});

export const ExportableLoan = z
  .object({
    id: LosId,
    // Lake column aliased to `company_id`: it's a foreign key, but the exportable JSON key stays
    // `company` for the data-export contract. Same pattern for the borrower/contact FKs below.
    company: lakeColumn(CompanyId, 'company_id'),
    address: ExportableAddress,
    originalAmount: z.number(),
    // raw: object; // TODO
    poolId: PoolId.optional(),
    previousServicerId: z.string().optional(),
    capitalPartnerId: z.string().optional(),
    fullId: z.string().optional(),
    otherId1: z.string().optional(),
    otherId2: z.string().optional(),
    otherId3: z.string().optional(),
    ownerId: LoanOwnerId.optional(), // some loans currently have non-uuid values
    noteflowId: z.string().optional(),
    primaryBorrower: lakeColumn(BorrowerId, 'primary_borrower_id'),
    additionalBorrowers: lakeColumn(BorrowerId.array(), 'additional_borrower_ids'),
    entityId: EntityId.optional(),
    authorizedContacts: lakeColumn(AuthorizedContactId.array(), 'authorized_contact_ids'),
    // externalIdHistory: { // TODO
    //   poolId: ExternalIdHistory[];
    //   previousServicerId: ExternalIdHistory[];
    //   capitalPartnerId: ExternalIdHistory[];
    //   fullId: ExternalIdHistory[];
    //   otherId1: ExternalIdHistory[];
    //   otherId2: ExternalIdHistory[];
    //   otherId3: ExternalIdHistory[];
    //   ownerId: ExternalIdHistory[];
    // };
    createdAt: zodTemporalInstant,
    updatedAt: zodTemporalInstant,
    archivedAt: zodTemporalInstant.optional(),

    // -- Loan configuration (static values from loan.configuration) --
    paymentType: z.enum(['fixed', 'arm']).optional(),
    productType: z.enum(PRODUCT_TYPES).optional(),
    loanTerm: z.int().optional(),
    fundingDate: zodTemporalPlainDate.optional(),
    loanClosingDate: zodTemporalPlainDate.optional(),
    firstDueDate: zodTemporalPlainDate.optional(),
    initialMaturityDate: zodTemporalPlainDate.optional(),
    noteDate: zodTemporalPlainDate.optional(),
    purpose: z.enum(['purchase', 'refinance']).optional(),
    occupancyType: z.enum(['primary', 'second', 'investment']).optional(),
    isEscrowed: z.boolean().optional(),
    lienPosition: z.int().optional(),
    acquisitionDate: zodTemporalPlainDate.optional(),
    gracePeriod: z.number().optional(),
    paymentFrequency: z.union([z.literal(1), z.literal(12)]).optional(),
    hasInterestReserve: z.boolean().optional(),
    qualifiedMortgage: z.boolean().optional(),
    maxLineOfCredit: z.number().optional(),
    fundingChannel: z.enum(['broker', 'miniCorrespondent', 'correspondent']).optional(),

    // -- Investor --
    investorName: z.string().optional(),
    investorLoanId: z.string().optional(),
    fhaCaseNumber: z.string().optional(),

    // -- Current state (authoritative values from snapshot) --
    loanStatus: z.string().optional(),
    // Derived from the current PaymentConfig entry. Sourced from PaymentConfig.monthlyInterestRate
    // which is misnamed — the value is actually an annual rate. See https://github.com/willowservicing/willow/pull/10360
    currentInterestRate: z.number().optional(),
    maturityDate: zodTemporalPlainDate.optional(), // from maturityConfig.history (current, not initial)
    outstandingPrincipalBalance: z.number().optional(),
    escrowBalance: z.number().optional(),
    reserveBalance: z.number().optional(),
    lastPaymentDate: zodTemporalPlainDate.optional(),
    paidOffDate: zodTemporalPlainDate.optional(),
    transferEffectiveDate: zodTemporalPlainDate.optional(),
    reoDate: zodTemporalPlainDate.optional(),
    mipLTVThresholdDate: zodTemporalPlainDate.optional(),
    specialLoanStatus: SpecialLoanStatus.optional(),
    nonPerformingStatus: NonPerformanceStatus.optional(),
    statementSuspenseBalance: z.number().optional(),
    futureStatementSuspenseBalance: z.number().optional(),
    unappliedSuspenseBalance: z.number().optional(),
    holdbackBalance: z.number().optional(),
    lossDraftBalance: z.number().optional(),
    deferredAmounts: ExportableDeferredAmounts.optional(),
    feeBalance: z.number().optional(),
    feesOutstanding: z.number().optional().describe('Excludes deferred fees.'),
    feesOutstandingIncludingDeferred: z.number().optional(),
    advancesPrincipalOutstanding: z.number().optional(),
    legalTransferEffectiveDate: zodTemporalPlainDate
      .optional()
      .describe(
        'Legally-effective transfer date used in borrower communications; distinct from transferEffectiveDate.',
      ),

    // -- Computed at index time (not raw loan model fields) --
    // The borrower's billed, unpaid total — the statement's "Total Amount Due".
    totalAmountDue: z.number().optional(),
    netDeferredBalance: z
      .number()
      .optional()
      .describe(
        "Deferred principal + interest across the loan's workout plans; distinct from deferredAmounts, the snapshot's running tally.",
      ),
  })
  .meta({ title: 'Loan' });

export type ExportableLoan = z.infer<typeof ExportableLoan>;
